import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type ToolAppKey = 'motortwin' | 'fmeda';

export interface OrgAppAccess {
    motortwin: boolean;
    fmeda: boolean;
    materials: boolean;
}

@Injectable()
export class OrganisationService {
    private org$ = new BehaviorSubject<any>({});
    currentOrg$ = this.org$.asObservable();
    private activeApp$ = new BehaviorSubject<ToolAppKey | null>(null);
    currentApp$ = this.activeApp$.asObservable();
    private readonly appAccessStorageKey = 'org.app.access.v1';

    constructor() { }

    setOrg(org: any) {
        this.org$.next(org);
    }

    setActiveApp(app: ToolAppKey | null) {
        this.activeApp$.next(app);
    }

    getCurrentOrg(): any {
        return this.org$.value;
    }

    getDefaultAppAccess(): OrgAppAccess {
        return {
            motortwin: true,
            fmeda: true,
            materials: true,
        };
    }

    getOrgAppAccess(org: any): OrgAppAccess {
        const defaultAccess = this.getDefaultAppAccess();
        const orgId = org && org.id ? org.id : '';
        if (!orgId) {
            return defaultAccess;
        }

        const map = this.getStoredAppAccessMap();
        const rawAccess = map[orgId];
        if (!rawAccess) {
            return defaultAccess;
        }

        return {
            motortwin: rawAccess.motortwin !== false,
            fmeda: rawAccess.fmeda !== false,
            materials: rawAccess.materials !== false,
        };
    }

    getCurrentOrgAppAccess(): OrgAppAccess {
        return this.getOrgAppAccess(this.getCurrentOrg());
    }

    isAppEnabledForCurrentOrg(app: ToolAppKey): boolean {
        return this.getCurrentOrgAppAccess()[app];
    }

    setOrgAppAccess(orgId: string, access: OrgAppAccess) {
        if (!orgId) {
            return;
        }

        const map = this.getStoredAppAccessMap();
        map[orgId] = {
            motortwin: access.motortwin !== false,
            fmeda: access.fmeda !== false,
            materials: access.materials !== false,
        };
        this.saveStoredAppAccessMap(map);
    }

    private getStoredAppAccessMap(): Record<string, OrgAppAccess> {
        if (typeof window === 'undefined' || !window.localStorage) {
            return {};
        }

        const raw = window.localStorage.getItem(this.appAccessStorageKey);
        if (!raw) {
            return {};
        }

        try {
            return JSON.parse(raw);
        }
        catch {
            return {};
        }
    }

    private saveStoredAppAccessMap(map: Record<string, OrgAppAccess>) {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        window.localStorage.setItem(this.appAccessStorageKey, JSON.stringify(map));
    }
}